import type { Claim, KnowledgeBase } from "./domain";
import { publicBaseUrl } from "./site";

export type CompiledArtifacts = Record<string, string>;

export { publicBaseUrl } from "./site";

const evidenceDescriptions = {
  official: "Directly supported by a first-party release, model card, repository, or research report.",
  independent: "Reproduced or evaluated by a source independent of the originating organization.",
  vendor_claim: "Asserted by the originating organization but not independently verified in this ledger.",
  rumor: "Unconfirmed reporting or community evidence that should not be treated as established fact.",
} as const;

const newestFirst = <T extends { effectiveAt?: string; changedAt?: string }>(records: T[]): T[] =>
  [...records].sort((left, right) =>
    (right.effectiveAt ?? right.changedAt ?? "").localeCompare(left.effectiveAt ?? left.changedAt ?? ""),
  );

const markdownForClaim = (claim: Claim): string => {
  const sources = claim.sources
    .map((source) => `  - [${source.publisher}: ${source.title}](${source.url}) (${source.sourceKind})`)
    .join("\n");
  return [
    `## ${claim.title}`,
    "",
    claim.summary,
    "",
    `- Evidence: ${claim.evidenceClass}`,
    `- Status: ${claim.status}`,
    `- Effective: ${claim.effectiveAt}`,
    `- Verified: ${claim.verifiedAt}`,
    `- Topics: ${claim.topics.join(", ")}`,
    "- Sources:",
    sources,
  ].join("\n");
};

const escapeXml = (value: string): string =>
  value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");

export const currentClaims = (knowledgeBase: KnowledgeBase): Claim[] =>
  newestFirst(knowledgeBase.claims.filter((claim) => claim.status === "current"));

export const renderCurrentMarkdown = (knowledgeBase: KnowledgeBase, claims = currentClaims(knowledgeBase)): string => {
  const evidence = Object.entries(evidenceDescriptions)
    .map(([label, description]) => `- **${label}** — ${description}`)
    .join("\n");
  return [
    "# Frontier State — Current AI Brief",
    "",
    `Verified through: ${knowledgeBase.verifiedThrough}`,
    "",
    "Frontier State is a temporal, source-ranked knowledge layer. A listing here means the underlying event was checked; it does not promote every performance statement inside a vendor release to independent fact.",
    "",
    "## Evidence classes",
    "",
    evidence,
    "",
    ...claims.flatMap((claim) => [markdownForClaim(claim), ""]),
  ].join("\n");
};

const renderFeed = (knowledgeBase: KnowledgeBase): string => {
  const claims = new Map(knowledgeBase.claims.map((claim) => [claim.id, claim]));
  const items = newestFirst(knowledgeBase.changes)
    .map((change) => {
      const claim = claims.get(change.claimId);
      if (!claim) throw new Error(`change references unknown claim: ${change.claimId}`);
      return [
        "<item>",
        `<guid>${escapeXml(change.id)}</guid>`,
        `<title>${escapeXml(claim.title)}</title>`,
        `<description>${escapeXml(change.note)}</description>`,
        `<link>${escapeXml(claim.sources[0]?.url ?? `${publicBaseUrl}/`)}</link>`,
        `<pubDate>${new Date(change.changedAt).toUTCString()}</pubDate>`,
        "</item>",
      ].join("");
    })
    .join("");
  return `<?xml version="1.0" encoding="UTF-8"?><rss version="2.0"><channel><title>Frontier State changes</title><link>${publicBaseUrl}/</link><description>Verified changes in the state of frontier AI.</description>${items}</channel></rss>\n`;
};

export const compileKnowledgeBase = (knowledgeBase: KnowledgeBase): CompiledArtifacts => {
  const claims = currentClaims(knowledgeBase);
  const current = {
    schemaVersion: 1,
    generatedAt: knowledgeBase.verifiedThrough,
    verifiedThrough: knowledgeBase.verifiedThrough,
    evidenceClasses: evidenceDescriptions,
    claims,
  };
  const changes = {
    schemaVersion: 1,
    generatedAt: knowledgeBase.verifiedThrough,
    changes: newestFirst(knowledgeBase.changes),
  };
  const catalog = {
    ...current,
    entities: knowledgeBase.entities,
    changes: changes.changes,
  };
  const llms = [
    "# Frontier State",
    "",
    "> A continuously verified, temporal state of the AI frontier for humans and machines.",
    "",
    `Last verified: ${knowledgeBase.verifiedThrough}`,
    "",
    "## Canonical resources",
    "",
    `- [Current brief](${publicBaseUrl}/now.md): Human and LLM-readable current state with evidence classes and citations.`,
    `- [Current JSON](${publicBaseUrl}/now.json): Structured current claims and source metadata.`,
    `- [Change feed](${publicBaseUrl}/changes.json): Append-only chronological ledger.`,
    `- [RSS](${publicBaseUrl}/feed.xml): Syndicated change feed.`,
    `- [Context API](${publicBaseUrl}/api/context): Bounded context packets filtered by topic and date.`,
    "",
    "## Interpretation",
    "",
    "Keep official events, independent evidence, vendor claims, and rumors distinct. Prefer primary sources for release facts and independent sources for performance conclusions.",
    "",
  ].join("\n");
  return {
    "now.md": `${renderCurrentMarkdown(knowledgeBase, claims)}\n`,
    "now.json": `${JSON.stringify(current, null, 2)}\n`,
    "changes.json": `${JSON.stringify(changes, null, 2)}\n`,
    "llms.txt": llms,
    "feed.xml": renderFeed(knowledgeBase),
    "data/catalog.json": `${JSON.stringify(catalog, null, 2)}\n`,
  };
};
